import React, { useMemo, useState } from 'react'
import {
    Tooltip,
    IconButton,
    Badge,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    Grid,
    Box,
    Typography,
    Switch,
    FormControlLabel,
} from '@material-ui/core'
import { Container, ScheduleReportPopOver } from '../../../components'
import { SchedulerHeaderTypeOfAnalytics } from './SchedulerHeaderTypeOfAnalytics'

// Icons
// Custom
import { ReactComponent as UnitNotesIcon } from '../../../assets/icons/unitnotes.svg'

// MUI
import {
    BarChart,
    Add,
    Tune,
    Edit,
    MonetizationOn,
    Check,
    VpnKey,
    FileCopy,
    Link,
    Lock,
    LockOpen,
    Ballot,
    Refresh,
    Launch,
    SwapHoriz,
} from '@material-ui/icons'

import { hasPermission } from '../../../models/Users/services'
import {
    AreaStatusTag,
    BaseWorkorder,
    ChangeOrderStatus,
    CREATE_WORKORDERS,
    EDIT_WORKORDERS,
    Schedule,
    SEE_VENDOR_FINANCES,
} from '../../../models'
import { axiosInstance } from '../../../helpers'
import FileDownload from 'js-file-download'
import { ChangeOrderFilterMode, RootState } from '../../../store'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { SchedulePreTurnChecklist } from './SchedulePreTurnChecklist'
import { DoubleTransferChainModal } from '../Modals/DoubleTransferChainModal'
import { useUser } from '../../../hooks'
import { useSchedulerHeaderStyles } from './styles'

interface Props {
    filterOpen: boolean
    editMode: boolean
    clickAssign: () => void
    clickBudget: () => void
    clickFilter: () => void
    clickAnalytics: () => void
    analyticsOpen: boolean
    setEditMode: (editMode: boolean) => void
    clickKeyTracking: () => void
    schedule?: Schedule
    visibleInfrastructureUnitCount: number
    visibleInfrastructureAreaCount: number
    visibleAreaConfigBreakdown: { [name: string]: number }
    ghostServiceAreasCount: number
    workorderList?: BaseWorkorder[]
    areaStatusTagList?: AreaStatusTag[]
    openProjectNotes: boolean
    clickProjectNotes: () => void
    clickChangeOrder: () => void
    downloadUnitNotesReport: () => void
    bulkLockAreaStatusTags: (locked: boolean) => void
    refreshWorkorders: () => void
    isSpreadsheetView: boolean
    setIsSpreadsheetView: (value: boolean) => void
    viewAnalyticsTab: number
    setViewAnalyticsTab: (tab: number) => void
    clickScheduleReview: () => void
    scheduleReviewEnabled: boolean
    dtChainOpen: boolean
    dtChainAlertMode: boolean
    onDtChainOpen: () => void
    onDtChainClose: () => void
}

export const SchedulerHeaderNav = (props: Props) => {
    const {
        schedule,
        clickKeyTracking,
        clickScheduleReview,
        scheduleReviewEnabled,
        refreshWorkorders,
        isSpreadsheetView,
        setIsSpreadsheetView,
        viewAnalyticsTab,
        setViewAnalyticsTab,
        dtChainOpen,
        dtChainAlertMode,
        onDtChainOpen,
        onDtChainClose,
    } = props
    const classes = useSchedulerHeaderStyles()
    const filter = useSelector(
        (state: RootState) => state.infrastructureFilter.filter,
    )
    const { workspaceUser } = useUser()

    const areaConfigBreakdownEntries = useMemo(
        () =>
            Object.entries(props.visibleAreaConfigBreakdown).sort((a, b) =>
                a[0].localeCompare(b[0]),
            ),
        [props.visibleAreaConfigBreakdown],
    )

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
    const [preTurnAnchorEl, setPreTurnAnchorEl] = useState<HTMLElement | null>(
        null,
    )
    const [reportLoading, setReportLoading] = useState(false)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [pendingLockAction, setPendingLockAction] = useState<boolean | null>(
        null,
    )

    const openPreTurnPopover = (event: React.MouseEvent<HTMLElement>) => {
        setPreTurnAnchorEl(event.currentTarget)
    }
    const closePreTurnPopover = () => {
        setPreTurnAnchorEl(null)
    }
    const preTurnPopoverOpen = Boolean(preTurnAnchorEl)

    const openPopover = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setAnchorEl(null)
    }

    const popOverOpen = Boolean(anchorEl)

    const downloadWorkorderTransitionReport = () => {
        if (!props.schedule?.id) return
        setReportLoading(true)
        axiosInstance
            .post(
                'stats/workorder-report/',
                {
                    schedule: props.schedule.id,
                    workorder_list: props.workorderList?.map((wo) => wo.id),
                    variant: 'workorder_transition',
                },
                { responseType: 'blob' },
            )
            .then((response) => {
                FileDownload(
                    new Blob([response.data]),
                    `${props.schedule?.name}_workorder_transition_report.xlsx`,
                )
            })
            .catch(() => {
                toast.error('There was a problem downloading your report')
            })
            .finally(() => {
                setReportLoading(false)
            })
    }

    const pendingChangeOrderCount = props.workorderList?.reduce(
        (prevCount, wo) => {
            return (prevCount +=
                wo.changeorder_set?.reduce((coCount, co) => {
                    return (coCount +=
                        co.status === ChangeOrderStatus.PENDING ? 1 : 0)
                }, 0) ?? 0)
        },
        0,
    )

    const lockAllAreas = useMemo(() => {
        return props.areaStatusTagList?.every((ast) => ast.locked)
    }, [props.areaStatusTagList])

    // Count areas that will be affected by the lock/unlock action
    const getAffectedAreasCount = useMemo(() => {
        if (!props.areaStatusTagList) return 0

        if (lockAllAreas) {
            // If all areas are locked, count areas that will be unlocked
            return props.areaStatusTagList.filter((ast) => ast.locked).length
        } else {
            // If not all areas are locked, count areas that will be locked
            return props.areaStatusTagList.filter((ast) => !ast.locked).length
        }
    }, [props.areaStatusTagList, lockAllAreas])

    const handleLockUnlockClick = () => {
        const newLockState = !lockAllAreas
        setPendingLockAction(newLockState)
        setConfirmDialogOpen(true)
    }

    const handleConfirmLockUnlock = () => {
        if (pendingLockAction !== null) {
            props.bulkLockAreaStatusTags(pendingLockAction)
            setConfirmDialogOpen(false)
            setPendingLockAction(null)
        }
    }

    const handleCancelLockUnlock = () => {
        setConfirmDialogOpen(false)
        setPendingLockAction(null)
    }

    let keyInCount = 0
    let keysRequired = 0
    props.areaStatusTagList?.forEach((ast) => {
        if (
            ast.area.area_config.occupiable &&
            ast.area_status_config.should_service
        ) {
            if (ast.key) {
                keyInCount += 1
            }
            keysRequired += 1
        }
    })

    return (
        <Container className={classes.schedulerHeaderNav}>
            <Box className={classes.schedulerHeaderNavColumns}>
                <Container className={classes.schedulerHeaderNavLeft}>
                    <Grid container>
                        {/* Icon Buttons Row */}
                        <Grid item xs={12}>
                            <Container className={classes.scheduleHeaderIcons}>
                                <Tooltip title="Refresh Workorders">
                                    <IconButton
                                        onClick={refreshWorkorders}
                                        className={classes.iconButton}
                                    >
                                        <Refresh />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        isSpreadsheetView
                                            ? 'Spreadsheet view'
                                            : 'Calendar view'
                                    }
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isSpreadsheetView}
                                                onChange={(_, checked) =>
                                                    setIsSpreadsheetView(
                                                        checked,
                                                    )
                                                }
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                Spreadsheet
                                            </Typography>
                                        }
                                        labelPlacement="start"
                                        style={{
                                            marginLeft: 8,
                                            marginRight: 0,
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title={
                                        scheduleReviewEnabled
                                            ? 'Open Scheduler Review'
                                            : 'Must Schedule First'
                                    }
                                >
                                    <span>
                                        <IconButton
                                            onClick={clickScheduleReview}
                                            className={classes.iconButton}
                                            disabled={!scheduleReviewEnabled}
                                        >
                                            <Ballot />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {/* Project Notes */}
                                {schedule?.channel && (
                                    <Tooltip title="Project Notes">
                                        <IconButton
                                            onClick={props.clickProjectNotes}
                                            className={classes.iconButton}
                                        >
                                            <UnitNotesIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Lock All Areas */}
                                {schedule && lockAllAreas !== undefined && (
                                    <Tooltip
                                        title={
                                            lockAllAreas
                                                ? 'Unlock All Areas'
                                                : 'Lock All Areas'
                                        }
                                    >
                                        <IconButton
                                            onClick={handleLockUnlockClick}
                                            className={classes.iconButton}
                                        >
                                            {lockAllAreas ? (
                                                <LockOpen
                                                    className={
                                                        classes.iconButton
                                                    }
                                                />
                                            ) : (
                                                <Lock
                                                    className={
                                                        classes.iconButton
                                                    }
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {/* Checklist */}
                                {/* {schedule && (
                            <Tooltip title="Checklist">
                                <IconButton
                                    onClick={openPreTurnPopover}
                                    className={classes.iconButton}
                                >
                                    <Check className={classes.iconButton} />
                                </IconButton>
                            </Tooltip>
                        )} */}
                                {/* Budget Details */}
                                {/* {schedule && (
                            <Tooltip title="Budget Details">
                                <IconButton
                                    onClick={() => props.clickBudget()}
                                    className={classes.iconButton}
                                    disabled={
                                        props.editMode ||
                                        !hasPermission(
                                            workspaceUser,
                                            SEE_VENDOR_FINANCES,
                                        )
                                    }
                                >
                                    <MonetizationOn />
                                </IconButton>
                            </Tooltip>
                        )} */}
                                {/* Assign */}
                                {/* {schedule && schedule?.active && (
                            <Tooltip title="Assign">
                                <IconButton
                                    onClick={props.clickAssign}
                                    className={classes.iconButton}
                                    disabled={
                                        !hasPermission(
                                            workspaceUser,
                                            CREATE_WORKORDERS,
                                        )
                                    }
                                >
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )} */}

                                {/* Key tracking */}
                                <Container
                                    className={classes.keyTrackingWrap}
                                    style={{ flexDirection: 'column' }}
                                >
                                    <Tooltip title="Key Tracking">
                                        <IconButton
                                            className={
                                                classes.keyTrackingIconButton
                                            }
                                            onClick={clickKeyTracking}
                                        >
                                            <VpnKey />
                                        </IconButton>
                                    </Tooltip>

                                    <span className={classes.keyTrackingBadge}>
                                        {keyInCount}/{keysRequired}
                                    </span>
                                </Container>

                                {schedule && (
                                    <Tooltip title="Double Transfer Chains">
                                        <IconButton
                                            onClick={onDtChainOpen}
                                            className={classes.iconButton}
                                        >
                                            <SwapHoriz />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Reports */}
                                {schedule && (
                                    <Tooltip title="Reports / Setup">
                                        <IconButton
                                            onClick={openPopover}
                                            className={classes.iconButton}
                                        >
                                            <FileCopy />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Schedule Analytics */}
                                {schedule && (
                                    <Tooltip title="Schedule Analytics">
                                        <IconButton
                                            disabled={props.editMode}
                                            className={classes.iconButton}
                                            onClick={props.clickAnalytics}
                                            color={
                                                props.analyticsOpen
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                        >
                                            <BarChart />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Bulk Editor */}
                                {/* {schedule && schedule?.active && (
                            <Tooltip title="Bulk Editor">
                                <IconButton
                                    onClick={() => {
                                        props.setEditMode(!props.editMode)
                                    }}
                                    disabled={
                                        !hasPermission(
                                            workspaceUser,
                                            EDIT_WORKORDERS,
                                        )
                                    }
                                    color={
                                        props.editMode ? 'primary' : 'default'
                                    }
                                    className={classes.iconButton}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )} */}

                                {/* Pending Change Orders */}
                                {/* {schedule && (
                            <Tooltip title="Pending Change Orders">
                                <IconButton
                                    onClick={props.clickChangeOrder}
                                    color={
                                        filter.changeOrder ===
                                        ChangeOrderFilterMode.HasPending
                                            ? 'primary'
                                            : 'default'
                                    }
                                    className={classes.iconButton}
                                >
                                    <Badge
                                        badgeContent={
                                            pendingChangeOrderCount ?? 0
                                        }
                                        color="secondary"
                                    >
                                        <Link />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        )} */}
                                {/* Filter */}
                                {/* {schedule && (
                            <Tooltip title="Filter">
                                <IconButton
                                    onClick={props.clickFilter}
                                    className={classes.iconButton}
                                >
                                    <Tune
                                        color={
                                            props.filterOpen
                                                ? 'primary'
                                                : undefined
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                        )} */}
                            </Container>
                        </Grid>
                        {/* Key Stats Row */}
                        <Grid item xs={12}>
                            <Container className={classes.keyStatsRow}>
                                {/*  Unit Count */}
                                <Box className={classes.statTile}>
                                    <Tooltip title="Filtered Units in the workspace which have the visibility setting of always visible or schedule only.">
                                        <Box className={classes.statContent}>
                                            <Typography
                                                component="span"
                                                className={classes.statLabel}
                                            >
                                                Units
                                            </Typography>
                                            <Typography
                                                component="span"
                                                className={classes.statValue}
                                            >
                                                {
                                                    props.visibleInfrastructureUnitCount
                                                }
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                                {/* Total Service Areas */}
                                <Box className={classes.statTile}>
                                    <Tooltip
                                        title={
                                            <>
                                                <div>
                                                    Total count of service areas
                                                    based on the units included
                                                    in the filter.
                                                </div>
                                                {areaConfigBreakdownEntries.length >
                                                0 ? (
                                                    <div
                                                        style={{ marginTop: 4 }}
                                                    >
                                                        {areaConfigBreakdownEntries.map(
                                                            ([name, count]) => (
                                                                <div key={name}>
                                                                    {name}:{' '}
                                                                    {count}
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                ) : null}
                                            </>
                                        }
                                    >
                                        <Box className={classes.statContent}>
                                            <Typography
                                                component="span"
                                                className={classes.statLabel}
                                            >
                                                Service Areas
                                            </Typography>
                                            <Typography
                                                component="span"
                                                className={classes.statValue}
                                            >
                                                {
                                                    props.visibleInfrastructureAreaCount
                                                }
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                                {/* Ghost Service Areas */}
                                <Box className={classes.statTile}>
                                    <Tooltip title="Total count of service areas which have workorders that are missing one of the following: vendor, service, or start date.">
                                        <Box className={classes.statContent}>
                                            <Typography
                                                component="span"
                                                className={classes.statLabel}
                                            >
                                                Ghost Service Areas
                                            </Typography>
                                            <Typography
                                                component="span"
                                                className={classes.statValue}
                                            >
                                                {props.ghostServiceAreasCount}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Container>
                        </Grid>
                    </Grid>
                </Container>
                <Box className={classes.typeOfAnalyticsBoxWrap}>
                    <SchedulerHeaderTypeOfAnalytics
                        value={viewAnalyticsTab}
                        onChange={setViewAnalyticsTab}
                    />
                </Box>
            </Box>

            <ScheduleReportPopOver
                open={popOverOpen}
                onClose={closePopover}
                anchorEl={anchorEl}
                anchorOrigin={{
                    horizontal: 'left',
                    vertical: 'bottom',
                }}
                reportLoading={reportLoading}
                reportItems={[
                    {
                        title: 'Excel Workorder Report',
                        reportFileType: 'excel',
                        reportDelivery: 'download',
                        downloadLink: {
                            title: 'Download',
                            onClick: () => {
                                setReportLoading(true)
                                axiosInstance
                                    .post(
                                        'stats/workorder-report/',
                                        {
                                            schedule: props.schedule?.id,
                                            workorder_list: props.workorderList?.map(
                                                (wo) => wo.id,
                                            ),
                                        },
                                        { responseType: 'blob' },
                                    )
                                    .then((response) => {
                                        FileDownload(
                                            new Blob([response.data]),
                                            `${props.schedule?.name}_workorder_report.xlsx`,
                                        )
                                    })
                                    .finally(() => {
                                        setReportLoading(false)
                                    })
                            },
                        },
                    },
                    {
                        title: 'PDF Workorder Report',
                        titleSubtitle: '(w/ messages)',
                        reportFileType: 'pdf',
                        reportDelivery: 'email',
                        downloadLink: {
                            title: 'Email report',
                            onClick: () => {
                                setReportLoading(true)
                                axiosInstance
                                    .post('stats/workorder-report/', {
                                        schedule: props.schedule?.id,
                                        workorder_list: props.workorderList?.map(
                                            (wo) => wo.id,
                                        ),
                                        variant: 'workorder_pdf',
                                    })
                                    .then((response) => {
                                        toast.success(response.data)
                                    })
                                    .finally(() => {
                                        setReportLoading(false)
                                    })
                            },
                        },
                    },
                    {
                        title: 'Workorder Transition Report',
                        reportFileType: 'excel',
                        reportDelivery: 'download',
                        downloadLink: {
                            title: 'Download',
                            onClick: downloadWorkorderTransitionReport,
                        },
                    },
                    {
                        title: 'Unit Report',
                        reportFileType: 'excel',
                        reportDelivery: 'download',
                        downloadLink: {
                            title: 'Download',
                            onClick: () => {
                                setReportLoading(true)
                                axiosInstance
                                    .post(
                                        'stats/workorder-report/',
                                        {
                                            schedule: props.schedule?.id,
                                            variant: 'unit',
                                            tz_offset: new Date().getTimezoneOffset(),
                                        },
                                        { responseType: 'blob' },
                                    )
                                    .then((response) => {
                                        FileDownload(
                                            new Blob([response.data]),
                                            `${props.schedule?.name}_unit_report.xlsx`,
                                        )
                                    })
                                    .finally(() => {
                                        setReportLoading(false)
                                    })
                            },
                        },
                    },
                    {
                        title: 'Unit Notes Report',
                        reportFileType: 'pdf',
                        reportDelivery: 'email',
                        downloadLink: {
                            title: 'Email report',
                            onClick: () => {
                                setReportLoading(true)
                                void Promise.resolve(
                                    props.downloadUnitNotesReport(),
                                ).finally(() => setReportLoading(false))
                            },
                        },
                    },
                ]}
            />
            {schedule && (
                <DoubleTransferChainModal
                    open={dtChainOpen}
                    schedule={schedule}
                    alertMode={dtChainAlertMode}
                    onClose={onDtChainClose}
                />
            )}
            {schedule && (
                <SchedulePreTurnChecklist
                    open={preTurnPopoverOpen}
                    schedule={schedule}
                    onClose={closePreTurnPopover}
                    anchorEl={preTurnAnchorEl}
                    anchorOrigin={{
                        horizontal: 'left',
                        vertical: 'bottom',
                    }}
                />
            )}

            {/* Confirmation Dialog for Lock/Unlock Areas */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelLockUnlock}
                aria-labelledby="lock-unlock-dialog-title"
                aria-describedby="lock-unlock-dialog-description"
            >
                <DialogTitle id="lock-unlock-dialog-title">
                    {pendingLockAction ? 'Lock All Areas' : 'Unlock All Areas'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="lock-unlock-dialog-description">
                        Are you sure you want to{' '}
                        {pendingLockAction ? 'lock' : 'unlock'}{' '}
                        {getAffectedAreasCount} area
                        {getAffectedAreasCount !== 1 ? 's' : ''}?
                        {pendingLockAction
                            ? ' This will lock all currently unlocked areas.'
                            : ' This will unlock all currently locked areas.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelLockUnlock} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmLockUnlock}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        {pendingLockAction ? 'Lock' : 'Unlock'} Areas
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
